/**
 * Builders for Canton/Daml Ledger API Value JSON used in prepareExecute choice arguments.
 * Canton wallets receive JSON shaped like protobuf-defined Daml `Value` nodes.
 */
export type DamlValueJson =
  | { party: string }
  | { text: string }
  | { numeric: string }
  | { contractId: string }
  | { timestamp: string }
  | { bool: boolean }
  | { list: { elements: DamlValueJson[] } }
  | { record: { fields: Array<{ label: string; value: DamlValueJson }> } }
  | { textMap: { entries: Array<{ key: string; value: DamlValueJson }> } }
  | { variant: { constructor: string; value: DamlValueJson } };

type DamlValueField = [label: string, value: DamlValueJson];
type ChoiceContextScalar = string | number | boolean | null;
type ChoiceContextObject = { [key: string]: ChoiceContextScalar };

type TaggedChoiceContextValue =
  | string
  | { tag: "AV_Text"; value: string }
  | { tag: "AV_ContractId"; value: string }
  | { tag: "AV_Bool"; value: boolean }
  | { tag: "AV_List"; value: TaggedChoiceContextValue[] }
  | {
      tag: string;
      value:
        | ChoiceContextScalar
        | TaggedChoiceContextValue[]
        | ChoiceContextObject;
    };

export const vParty = (party: string): DamlValueJson => ({ party });
export const vText = (text: string): DamlValueJson => ({ text });
export const vNumeric = (numeric: string): DamlValueJson => ({ numeric });
export const vContractId = (contractId: string): DamlValueJson => ({
  contractId,
});
export const vList = (elements: DamlValueJson[]): DamlValueJson => ({
  list: { elements },
});
export const vRecord = (fields: DamlValueField[]): DamlValueJson => ({
  record: { fields: fields.map(([label, value]) => ({ label, value })) },
});
export const vTextMap = (
  entries: Array<{ key: string; value: DamlValueJson }>
): DamlValueJson => ({ textMap: { entries } });
export const vTimestamp = (isoString: string): DamlValueJson => ({
  timestamp: String(Math.floor(new Date(isoString).getTime() * 1000)),
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function normalizeChoiceContextValue(
  value: unknown
): TaggedChoiceContextValue | null {
  if (typeof value === "string") return value;
  if (!isRecord(value) || typeof value.tag !== "string") return null;

  switch (value.tag) {
    case "AV_Text":
    case "AV_ContractId":
      return typeof value.value === "string"
        ? { tag: value.tag, value: value.value }
        : null;
    case "AV_Bool":
      return typeof value.value === "boolean"
        ? { tag: value.tag, value: value.value }
        : null;
    case "AV_List":
      if (!Array.isArray(value.value)) return null;
      return {
        tag: value.tag,
        value: value.value.flatMap((entry) => {
          const normalized = normalizeChoiceContextValue(entry);
          return normalized ? [normalized] : [];
        }),
      };
    default: {
      const inner = value.value;
      if (
        typeof inner === "string" ||
        typeof inner === "number" ||
        typeof inner === "boolean" ||
        inner === null
      ) {
        return { tag: value.tag, value: inner };
      }

      if (Array.isArray(inner)) {
        return {
          tag: value.tag,
          value: inner.flatMap((entry) => {
            const normalized = normalizeChoiceContextValue(entry);
            return normalized ? [normalized] : [];
          }),
        };
      }

      if (!isRecord(inner)) return null;

      const normalizedObject: Record<string, ChoiceContextScalar> = {};
      for (const [key, entry] of Object.entries(inner)) {
        if (
          typeof entry !== "string" &&
          typeof entry !== "number" &&
          typeof entry !== "boolean" &&
          entry !== null
        ) {
          return null;
        }
        normalizedObject[key] = entry;
      }
      return { tag: value.tag, value: normalizedObject };
    }
  }
}

function stringifyChoiceContextValue(
  value:
    | ChoiceContextScalar
    | TaggedChoiceContextValue[]
    | ChoiceContextObject
): string {
  return typeof value === "string" ? value : String(value);
}

export function choiceContextValueToAnyValue(
  value: TaggedChoiceContextValue
): DamlValueJson {
  if (typeof value === "string") {
    return {
      variant: { constructor: "AV_Text", value: vText(value) },
    };
  }

  switch (value.tag) {
    case "AV_ContractId":
      if (typeof value.value !== "string") {
        throw new TypeError("AV_ContractId values must be strings");
      }
      return {
        variant: { constructor: value.tag, value: vContractId(value.value) },
      };
    case "AV_Bool":
      if (typeof value.value !== "boolean") {
        throw new TypeError("AV_Bool values must be booleans");
      }
      return {
        variant: { constructor: value.tag, value: { bool: value.value } },
      };
    case "AV_List":
      if (!Array.isArray(value.value)) {
        throw new TypeError("AV_List values must be arrays");
      }
      return {
        variant: {
          constructor: value.tag,
          value: vList(value.value.map(choiceContextValueToAnyValue)),
        },
      };
    default:
      return {
        variant: {
          constructor: value.tag,
          value: vText(stringifyChoiceContextValue(value.value)),
        },
      };
  }
}
